<!--
Performance moment specifically for end of year, which supports
special conversations with connections, uploading documents, and preparation conversations
-->
<template>
    <AddPeopleDialog v-if="canEditTeam" v-model:dialog="showReviewDialog" />
    <ReviewDocumentUploadDialog
        v-if="currentReview"
        v-model:dialog="showDocumentUploadDialog"
        :review="currentReview"
        @save="handleDocumentDialogComplete"
        @cancel="currentReviewId = null" />

    <!-- Active moment content -->
    <div class="max-w-4xl mx-auto w-full px-0 md:px-4">
        <BaseNotification v-if="syncFailed" theme="error" hide-dismiss class="mt-6" :text="t('moments.endOfYear.syncFailed')" />

        <ReviewManagerSelectionCard v-if="momentHasOtherCTAs && hasPeopleToReview == null && !isMomentExpired" class="mb-10" :moment-id="moment.id" :moment-title="moment.title" />

        <LoadingSpinner v-if="isLoadingReviews" class="w-10 h-10 mx-auto my-10" />
        <template v-else>
            <!-- Self Evaluation Card -->
            <SelfEvaluationCard
                v-if="shouldShowSelfSection"
                class="mt-10"
                :reviews="selfReviews"
                :due-date="moment.self_moment?.due_date"
                :start-date="moment.self_moment?.start_date"
                :self-title="selfTitle"
                :self-subtitle="moment.self_moment?.subtitle"
                :current-review-id="currentReviewId"
                :is-moment-read-only="isMomentReadOnly"
                @cta-convo-clicked="handleConvoClick"
                @completion-toggled="handleCompletionToggled" />
            <!-- Team evaluation Card -->
            <MyPeopleEvaluationCard
                v-if="shouldShowOthersSection"
                ref="teamCard"
                class="mt-10"
                :reviews="otherReviews"
                :due-date="moment.other_moment?.due_date"
                :start-date="moment.other_moment?.start_date"
                :other-title="otherTitle"
                :other-subtitle="moment.other_moment?.subtitle"
                :moment-id="moment.id"
                :can-stop-reviewing="!isMomentExpired"
                :current-review-id="currentReviewId"
                :is-moment-read-only="isMomentReadOnly"
                :is-resolving-team-member="pendingRelationshipId !== null"
                :can-edit-team="canEditTeam"
                @edit-list="showReviewDialog = true"
                @stop-reviewing-others="handleStopReviewingOthers"
                @cta-convo-clicked="handleConvoClick"
                @completion-toggled="handleCompletionToggled"
                @confirm-relationship="handleConfirmRelationship"
                @decline-relationship="handleDeclineRelationship" />
            <!-- Opting out lives in the team section's menu; this only offers opting back in. -->
            <HasPeopleToReviewSwitcher
                v-if="momentHasOtherCTAs && !isMomentExpired && !hasPeopleToReview"
                ref="reviewSwitcher"
                :has-people-to-review="hasPeopleToReview"
                :moment-id="moment.id"
                :moment-title="moment.title"
                @toggled="handleOptedBackIn" />
        </template>
        <BaseToast v-model:open="isErrorToastOpen" theme="error" :description="errorMessage" />
    </div>
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AddPeopleDialog from "~vue/Moments/AddPeopleDialog.vue";
import { useHasPeopleToReviewToggle } from "~vue/Moments/composables/useHasPeopleToReviewToggle";
import { usePerformanceReviewConversations } from "~vue/Moments/composables/usePerformanceReviewConversations";
import { usePerformanceReviewRelationships } from "~vue/Moments/composables/usePerformanceReviewRelationships";
import { usePerformanceReviews } from "~vue/Moments/composables/usePerformanceReviews";
import { usePerformanceReviewSync } from "~vue/Moments/composables/usePerformanceReviewSync";
import { decorateReviews } from "~vue/Moments/composables/useReviewGroupStatus";
import { startConversationErrorMessage, useStartPerformanceReviewConversation } from "~vue/Moments/composables/useStartPerformanceReviewConversation";
import { PERFORMANCE_REVIEW_EVENT_PREFIX, REVIEW_TYPES } from "~vue/Moments/constants";
import MyPeopleEvaluationCard from "~vue/Moments/MyPeopleEvaluationCard.vue";
import ReviewDocumentUploadDialog from "~vue/Moments/ReviewDocumentUploadDialog.vue";
import ReviewManagerSelectionCard from "~vue/Moments/ReviewManagerSelectionCard.vue";
import SelfEvaluationCard from "~vue/Moments/SelfEvaluationCard.vue";
import type { DecoratedPerformanceReview, PerformanceMoment } from "~vue/types/performance_management";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import HasPeopleToReviewSwitcher from "./HasPeopleToReviewSwitcher.vue";

const {
    moment,
    hasPeopleToReview,
    canEditTeam = true,
    reviewEntryEnabled = false,
} = defineProps<{
    moment: PerformanceMoment;
    hasPeopleToReview: boolean | null;
    canEditTeam?: boolean;
    reviewEntryEnabled?: boolean;
}>();

const { t } = useI18n();

const isLoadingReviews = ref(true);

const selfReviews = computed(() => (reviews.value ?? []).filter((review) => review.cta?.reviewer_target == "self"));
const otherReviews = computed(() => (reviews.value ?? []).filter((review) => review.cta?.reviewer_target == "connection"));
const momentHasOtherCTAs = computed(() => moment.ctas.some((cta) => cta.reviewer_target === "connection"));
const isMomentExpired = computed(() => moment.state === "expired");
const shouldShowSelfSection = computed(() => !!selfReviews.value.length);
const shouldShowOthersSection = computed(() => hasPeopleToReview && momentHasOtherCTAs.value);

const selfTitle = computed(() => {
    if (moment.self_moment?.title) {
        return moment.self_moment.title;
    }
    return shouldShowOthersSection.value ? t("moments.endOfYear.selfSectionTitle") : "";
});
const otherTitle = computed(() => {
    if (moment.other_moment?.title) {
        return moment.other_moment.title;
    }
    return shouldShowSelfSection.value ? t("moments.endOfYear.teamSectionTitle") : "";
});

const { data: performanceReviewRelationships, execute: fetchPerformanceReviewRelationships } = usePerformanceReviewRelationships();
const { data: performanceReviewsData, execute: fetchPerformanceReviews } = usePerformanceReviews(moment.id);
const { data: conversationsData, execute: fetchConversations } = usePerformanceReviewConversations(moment.id);
const { data: syncData, sync: syncPerformanceReviews, error: syncError, statusCode: syncStatusCode } = usePerformanceReviewSync();

// Flag snapshot sent by get_performance_management_props, logged with moment_viewed so a dead
// or half-rendered page can be diagnosed from analytics alone.
const pageFeatureFlags = (usePage().props.page_feature_flags ?? {}) as Record<string, boolean>;

const syncFailed = ref(false);

const handleMountSyncFailure = (error: unknown) => {
    // The session-ended dialog owns that messaging; a banner behind it would contradict it.
    if (isSessionEndedError(error)) {
        return;
    }
    logError(error ?? new Error("Performance review sync failed on mount"), {
        tags: { feature: "performance_management", action: "mount_sync" },
        context: { moment_id: moment.id, http_status: syncStatusCode.value ?? null },
    });
    syncFailed.value = true;
};

// Sync on mount, then fetch relationships, reviews, and conversations
onMounted(async () => {
    let syncSucceeded = false;
    try {
        await syncPerformanceReviews(moment.id);
        syncSucceeded = !syncError.value;
        if (!syncSucceeded) {
            handleMountSyncFailure(syncError.value);
        }
    } catch (error) {
        handleMountSyncFailure(error);
    }

    // Run even when the sync failed: these are GETs and can still succeed, and a page with
    // slightly stale reviews beats the dead shell users hit when a sync 403 aborted everything.
    try {
        await Promise.all([fetchPerformanceReviewRelationships(), fetchPerformanceReviews(), fetchConversations()]);
    } finally {
        isLoadingReviews.value = false;
    }

    // Seeding a team from HRIS answers "do you have people to review?", and it runs inside the
    // sync above — after the server rendered this prop. Reload the prop rather than shadowing it
    // in a local ref, so the switcher and this page never disagree about the answer.
    if (syncSucceeded && syncData.value && syncData.value.has_people_to_review !== hasPeopleToReview) {
        // The watcher below opens the Add people dialog on a false→true flip with an empty team,
        // which is right when the manager flipped the switcher themselves and wrong here: HRIS
        // just answered for them, so a modal they never opened would be the first thing they see.
        answeredBySeeding.value = true;
        router.reload({ only: ["has_people_to_review"] });
    }

    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_MOMENT_VIEWED, {
        moment_id: moment.id,
        review_count: reviews.value.length,
        sync_succeeded: syncSucceeded,
        ...pageFeatureFlags,
    });
});

const showReviewDialog = ref(false);
const answeredBySeeding = ref(false);
watch(
    () => hasPeopleToReview,
    (newValue, oldValue) => {
        if (answeredBySeeding.value) {
            answeredBySeeding.value = false;
            return;
        }
        // Open the dialog when the user transitions into needing to pick connections:
        // flipping the switcher on (false/null → true) with no team members yet, so they can add some
        if (canEditTeam && !oldValue && newValue && otherReviews.value.length === 0) {
            showReviewDialog.value = true;
        }
    },
);
const showDocumentUploadDialog = ref(false);

// Refetch relationships, reviews, and conversations when dialog closes
watch(showReviewDialog, async (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) {
        await syncPerformanceReviews(moment.id);
        await Promise.all([fetchPerformanceReviewRelationships(), fetchPerformanceReviews(), fetchConversations()]);
    }

    if (isOpen && !wasOpen) {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}edit_team_list_opened`, { moment_id: moment.id });
    }
});

const reviews = computed<Array<DecoratedPerformanceReview>>(() =>
    decorateReviews(
        performanceReviewsData.value?.reviews || [],
        moment.ctas,
        performanceReviewRelationships.value?.relationships || [],
        conversationsData.value?.conversations || [],
    ),
);

const currentReviewId = ref<string | null>(null);
const currentReview = computed(() => {
    if (!currentReviewId.value) {
        return null;
    }
    return reviews.value.find((r) => r.id === currentReviewId.value) || null;
});

const { launchError, startConversation } = useStartPerformanceReviewConversation();

const isErrorToastOpen = ref(false);
const errorMessage = ref("");

const showLaunchError = (message: string) => {
    errorMessage.value = message;
    isErrorToastOpen.value = true;
};

const { setHasPeopleToReview } = useHasPeopleToReviewToggle();

// Shows or hides the team section once the user's answer is saved, from the menu or the opt-in switcher.
const teamCard = useTemplateRef<InstanceType<typeof MyPeopleEvaluationCard>>("teamCard");

// Opting back in removes the focused switcher; hand focus to the returning team section's menu, unless the
// add-people dialog opened for an empty team, which then owns focus.
const handleOptedBackIn = () =>
    router.reload({
        only: ["has_people_to_review"],
        onSuccess: async () => {
            await nextTick();
            if (!showReviewDialog.value) {
                teamCard.value?.focusMenu();
            }
        },
    });

const reviewSwitcher = useTemplateRef<InstanceType<typeof HasPeopleToReviewSwitcher>>("reviewSwitcher");

const handleStopReviewingOthers = async () => {
    if (!(await setHasPeopleToReview(false, moment.id))) {
        showLaunchError(t("moments.reviewSwitcher.updateError"));
        return;
    }
    // Opting out removes the team section along with the focused menu trigger; hand focus to the opt-in button that replaces it.
    router.reload({
        only: ["has_people_to_review"],
        onSuccess: async () => {
            await nextTick();
            reviewSwitcher.value?.focus();
        },
    });
};

const startPerformanceReviewConversation = async (review: DecoratedPerformanceReview) => {
    try {
        const url = await startConversation(review);
        if (url) {
            openUrl(url);
        } else if (launchError.value) {
            showLaunchError(launchError.value);
        }
    } finally {
        currentReviewId.value = null;
    }
};

const handleConvoClick = async (reviewId: string) => {
    const review = reviews.value.find((r) => r.id === reviewId);
    if (!review) {
        return;
    }
    currentReviewId.value = reviewId;

    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}convo_clicked`, {
        moment_id: moment.id,
        review_id: reviewId,
        cta_id: review.performance_moment_cta_id,
        prompt_call_code: review.cta?.prompt_call_code,
        review_for: !review.relationship ? REVIEW_TYPES.SELF : REVIEW_TYPES.CONNECTIONS,
    });

    if (!review.conversation) {
        if (review.cta?.uploads_enabled && !reviewEntryEnabled) {
            showDocumentUploadDialog.value = true;
            logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PRE_CONVO_UPLOAD_DIALOG_VIEWED, {
                moment_id: moment.id,
                review_id: reviewId,
                cta_id: review.performance_moment_cta_id,
                prompt_call_code: review.cta?.prompt_call_code,
                review_for: !review.relationship ? REVIEW_TYPES.SELF : REVIEW_TYPES.CONNECTIONS,
            });
        } else {
            await startPerformanceReviewConversation(review);
        }
    } else if (review.conversation.coaching_session_url) {
        openUrl(review.conversation.coaching_session_url);
    } else {
        // The list API excludes sessionless conversations, so a missing URL means stale data —
        // navigating anywhere would just reload the page, so surface the failure instead.
        showLaunchError(startConversationErrorMessage());
        currentReviewId.value = null;
    }
};

const handleDocumentDialogComplete = async (uploadedDocumentCount: number) => {
    // After documents are uploaded or skipped, start the conversation
    if (currentReview.value) {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}pre_convo_upload_dialog_closed`, {
            uploaded_document_count: uploadedDocumentCount,
            moment_id: moment.id,
            review_id: currentReview.value.id,
            cta_id: currentReview.value.performance_moment_cta_id,
            prompt_call_code: currentReview.value.cta?.prompt_call_code,
            review_for: !currentReview.value.relationship ? REVIEW_TYPES.SELF : REVIEW_TYPES.CONNECTIONS,
        });

        await startPerformanceReviewConversation(currentReview.value);
    }
};

// One id drives both endpoints: the prompt offers exactly one decision at a time, and a shared
// ref keeps the two request URLs from drifting apart.
const pendingRelationshipId = ref<string | null>(null);
const relationshipUrl = computed(() => `/api/coach/performance_reviews/relationships/${pendingRelationshipId.value}/`);
const { execute: executeConfirmRelationship, error: confirmRelationshipError } = useFetch(
    computed(() => `${relationshipUrl.value}confirm/`),
    { immediate: false },
)
    .post()
    .json();
const { execute: executeDeclineRelationship, error: declineRelationshipError } = useFetch(relationshipUrl, { immediate: false }).delete();

const resolveDetectedTeamMember = async (relationshipId: string, accepted: boolean) => {
    // Several prompts can be on screen at once, and both verbs share one useFetch instance whose
    // execute() aborts the request in flight. Without this, answering a second prompt cancels the
    // first, whose handler then sees a cleared error ref and reports a success that never happened.
    if (pendingRelationshipId.value !== null) {
        return;
    }
    pendingRelationshipId.value = relationshipId;
    let error: unknown = null;
    try {
        await nextTick();
        if (accepted) {
            await executeConfirmRelationship();
            error = confirmRelationshipError.value;
        } else {
            await executeDeclineRelationship();
            error = declineRelationshipError.value;
        }
    } catch (thrown) {
        error = thrown;
    } finally {
        // In a finally so a throw cannot leave the guard held, which would make every later
        // prompt on the page silently do nothing.
        pendingRelationshipId.value = null;
    }

    if (error) {
        if (!isSessionEndedError(error)) {
            showLaunchError(t("components.moments.reviewTeam.updateFailed"));
        }
        return;
    }

    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}hris_team_member_${accepted ? "confirmed" : "declined"}`, {
        moment_id: moment.id,
        relationship_id: relationshipId,
    });

    // Declining deletes the relationship, and reviews cascade with it, so both lists are refetched
    // either way rather than only on the path that happens to change the most.
    await Promise.all([fetchPerformanceReviewRelationships(), fetchPerformanceReviews(), fetchConversations()]);
};

const handleConfirmRelationship = (relationshipId: string) => resolveDetectedTeamMember(relationshipId, true);
const handleDeclineRelationship = (relationshipId: string) => resolveDetectedTeamMember(relationshipId, false);

const isMomentReadOnly = computed(() => moment.state === "expired" || moment.state === "locked");

const handleCompletionToggled = async () => {
    await fetchPerformanceReviews();
};
</script>
